import { UUID } from "crypto";
import { baseApi } from "./baseApi";
import {
    AccountResponse,
    AdmoinCouncilPersonCreate,
    AdmonCouncilPerson,
    ContactEmailInfo,
    ContactInfo,
    ContactPhoneInfo,
    EmailType,
    PhoneType,
    ServiceMonthlyCatalog,
    ServiceMonthlyIncome,
    ServiceMonthlyOutcome,
} from "@interfaces/account";
import { Response, ResponsePagination } from "@interfaces/response";

const accountsApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        postAccount: build.mutation<AccountResponse, Partial<AccountResponse>>({
            query: (body) => ({
                url: "cuentas",
                method: "POST",
                body,
            }),
            transformResponse: (response: Response<AccountResponse>) => {
                return response.data;
            },
        }),
        patchAccount: build.mutation<unknown, { clientDetailsId: UUID; body: Partial<AccountResponse> }>({
            invalidatesTags: ["Application"],
            query: ({ clientDetailsId, body }) => ({
                url: `cuentas/${clientDetailsId}`,
                method: "PATCH",
                body,
            }),
        }),
        getAccount: build.query<AccountResponse, UUID>({
            query: (clientDetailsId) => ({
                url: `cuentas/${clientDetailsId}`,
            }),
            transformResponse: (response: Response<AccountResponse>) => {
                return response.data;
            },
        }),
        getAdmonCouncil: build.query<Array<AdmonCouncilPerson>, UUID>({
            query: (clientDetailsId) => ({
                url: `cuentas/${clientDetailsId}/administration_council`,
            }),
            transformResponse: (response: ResponsePagination<AdmonCouncilPerson>) => {
                return response.data.data;
            },
        }),
        postAdmonCouncilPerson: build.mutation<AdmonCouncilPerson, AdmoinCouncilPersonCreate>({
            query: (body) => ({
                url: `cuentas/${body.client_details_id}/administration_council`,
                method: "POST",
                body: body,
            }),
            transformResponse: (response: Response<AdmonCouncilPerson>) => {
                return response.data;
            },
        }),
        patchAdmonCouncilPerson: build.mutation<AdmonCouncilPerson, Partial<AdmonCouncilPerson>>({
            query: (body) => ({
                url: `cuentas/${body.client_details_id}/administration_council/${body.id}`,
                method: "PATCH",
                body: body,
            }),
            transformResponse: (response: Response<AdmonCouncilPerson>) => {
                return response.data;
            },
        }),
        deleteAdmonCouncilPerson: build.mutation<UUID, { clientDetailsId: UUID; memberId: UUID }>({
            query: ({ clientDetailsId, memberId }) => ({
                url: `cuentas/${clientDetailsId}/administration_council/${memberId}`,
                method: "DELETE",
            }),
            transformResponse: (response: Response<AdmonCouncilPerson>) => {
                return response.data.id;
            },
        }),
        getServiceMonthlyIncome: build.query<Array<ServiceMonthlyIncome>, UUID>({
            query: (clientDetailsId) => ({
                url: `cuentas/${clientDetailsId}/service_monthly_income`,
            }),
            transformResponse: (response: ResponsePagination<ServiceMonthlyIncome>) => {
                return response.data.data;
            },
        }),
        getServiceMonthlyOutcome: build.query<Array<ServiceMonthlyOutcome>, UUID>({
            query: (clientDetailsId) => ({
                url: `cuentas/${clientDetailsId}/service_monthly_outcome`,
            }),
            transformResponse: (response: ResponsePagination<ServiceMonthlyOutcome>) => {
                return response.data.data;
            },
        }),
        getContactInfo: build.query<ContactInfo, UUID>({
            query: (applicationId) => ({
                url: `cuentas/contact_validation/${applicationId}`,
            }),
            transformResponse: (response: Response<ContactInfo>) => {
                return response.data;
            },
        }),
        deleteContactPhone: build.mutation<ContactPhoneInfo, UUID>({
            query: (phoneId) => ({
                url: `cuentas/phones/${phoneId}`,
                method: "DELETE",
            }),
            transformResponse: (response: Response<ContactPhoneInfo>) => {
                return response.data;
            },
        }),
        patchContactPhone: build.mutation<ContactPhoneInfo, { phoneId: UUID; payload: Partial<ContactPhoneInfo> }>({
            query: ({ phoneId, payload }) => ({
                url: `cuentas/phones/${phoneId}`,
                method: "PATCH",
                body: payload,
            }),
            transformResponse: (response: Response<ContactPhoneInfo>) => {
                return response.data;
            },
        }),
        postContactPhone: build.mutation<ContactPhoneInfo, Partial<ContactPhoneInfo>>({
            query: (payload) => ({
                url: `cuentas/phones`,
                method: "POST",
                body: payload,
            }),
            transformResponse: (response: Response<ContactPhoneInfo>) => {
                return response.data;
            },
        }),
        getPhoneTypes: build.query<Array<PhoneType>, void>({
            query: () => ({
                url: "cuentas/phone_types",
            }),
            transformResponse: (repsonse: ResponsePagination<PhoneType>) => {
                return repsonse.data.data;
            },
        }),
        deleteContactEmail: build.mutation<ContactEmailInfo, UUID>({
            query: (emailId) => ({
                url: `cuentas/emails/${emailId}`,
                method: "DELETE",
            }),
            transformResponse: (response: Response<ContactEmailInfo>) => {
                return response.data;
            },
        }),
        postContactEmail: build.mutation<ContactEmailInfo, Partial<ContactEmailInfo>>({
            query: (payload) => ({
                url: `cuentas/emails`,
                method: "POST",
                body: payload,
            }),
            transformResponse: (response: Response<ContactEmailInfo>) => {
                return response.data;
            },
        }),
        patchContactEmail: build.mutation<ContactEmailInfo, { emailId: UUID; payload: Partial<ContactEmailInfo> }>({
            query: ({ emailId, payload }) => ({
                url: `cuentas/emails/${emailId}`,
                method: "PATCH",
                body: payload,
            }),
            transformResponse: (response: Response<ContactEmailInfo>) => {
                return response.data;
            },
        }),
        getEmailTypes: build.query<Array<EmailType>, void>({
            query: () => ({
                url: "cuentas/email_types",
            }),
            transformResponse: (repsonse: ResponsePagination<EmailType>) => {
                return repsonse.data.data;
            },
        }),
    }),
});

export const {
    usePostAccountMutation,
    usePatchAccountMutation,
    useGetAccountQuery,
    useLazyGetAccountQuery,
    useLazyGetAdmonCouncilQuery,
    usePostAdmonCouncilPersonMutation,
    usePatchAdmonCouncilPersonMutation,
    useDeleteAdmonCouncilPersonMutation,
    useGetServiceMonthlyIncomeQuery,
    useGetServiceMonthlyOutcomeQuery,
    useLazyGetContactInfoQuery,
    usePatchContactPhoneMutation,
    usePostContactPhoneMutation,
    useDeleteContactPhoneMutation,
    usePostContactEmailMutation,
    useDeleteContactEmailMutation,
    usePatchContactEmailMutation,
    useLazyGetEmailTypesQuery,
    useLazyGetPhoneTypesQuery,
} = accountsApi;
